import React from 'react';
import { FDIFlowsPanel } from '../components/panels/trade/FDIFlowsPanel';
import { FDISourcePanel } from '../components/panels/trade/FDISourcePanel';
import { TradeBalancePanel } from '../components/panels/trade/TradeBalancePanel';
import { TradePartnersPanel } from '../components/panels/trade/TradePartnersPanel';
import { ExportProductsPanel } from '../components/panels/trade/ExportProductsPanel';
import { usePanelManager, PanelConfig } from '../hooks/usePanelManager';
import { MinimizedBar } from '../components/ui/MinimizedBar';

// Panel configuration for Trade & External view
const panelConfig: PanelConfig[] = [
  { id: 'fdi-flows', title: 'FDI Flows', row: 1, colSpan: 2 },
  { id: 'fdi-source', title: 'FDI by Source', row: 1, colSpan: 2 },
  { id: 'trade-balance', title: 'Trade Balance', row: 2 },
  { id: 'trade-partners', title: 'Trade Partners', row: 2, colSpan: 2 },
  { id: 'export-products', title: 'Export Products', row: 2 },
];

// Panel component map
const panelComponents: Record<string, React.FC<{ isMinimized?: boolean; onMinimize?: () => void }>> = {
  'fdi-flows': FDIFlowsPanel,
  'fdi-source': FDISourcePanel,
  'trade-balance': TradeBalancePanel,
  'trade-partners': TradePartnersPanel,
  'export-products': ExportProductsPanel,
};

const TradeExternalView: React.FC = () => {
  const {
    minimizedPanelsList,
    handleMinimize,
    handleRestore,
    getOrderedPanels,
    isPanelMinimized,
    getDragState,
    getDragProps,
  } = usePanelManager(panelConfig);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <MinimizedBar panels={minimizedPanelsList} onRestore={handleRestore} />

      <div className="flex-1 flex flex-col md:grid md:grid-cols-2 lg:grid-cols-4 md:grid-rows-[1fr_1fr] gap-px p-px bg-terminal-border overflow-y-auto md:overflow-hidden">
        {/* Row 1 - FDI Flows & FDI by Source */}
        {getOrderedPanels(1).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[280px] sm:min-h-[320px] md:min-h-0 col-span-1 lg:col-span-2 h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}

        {/* Row 2 - Trade Balance, Partners, Export Products */}
        {getOrderedPanels(2).map(panel => (
          <div
            key={panel.id}
            {...getDragProps(panel.id)}
            className={`min-h-[280px] sm:min-h-[320px] md:min-h-0 ${
              panel.colSpan === 2 ? 'col-span-1 lg:col-span-2' : ''
            } h-full transition-all duration-200 ${
              getDragState(panel.id).isDragging ? 'opacity-50 scale-[0.98]' : ''
            } ${getDragState(panel.id).isDragOver ? 'ring-2 ring-data-amber/50 ring-inset' : ''}`}
          >
            {React.createElement(panelComponents[panel.id], {
              isMinimized: isPanelMinimized(panel.id),
              onMinimize: () => handleMinimize(panel.id),
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TradeExternalView;
